import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { fromEvent, interval, merge, of, Subject } from 'rxjs';
import { switchMap, filter, map, catchError, take } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private currentVersion: string | null = null;
  private updateNotified = false;
  private readonly checkRequested = new Subject<void>();

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  start(): void {
    this.fetchVersion().subscribe({
      next: version => {
        if (!version) return;
        this.currentVersion = version;
        this.startPolling();
      }
    });
  }

  /**
   * Forza un controllo immediato. La chiama NotificationBackendService quando arriva
   * l'evento SSE `app.version`, cioe' entro ~1s dal deploy: il polling a 5 minuti resta
   * come rete di sicurezza per chi non ha lo stream aperto (non autenticato, SSE caduto,
   * scheda riaperta dopo ore).
   *
   * Prima che start() abbia letto la versione iniziale, currentVersion e' null e il filtro
   * in startPolling() scarta comunque il risultato: nessun toast al primo caricamento.
   */
  checkNow(): void {
    this.checkRequested.next();
  }

  private startPolling(): void {
    // Il polling a intervallo non scatta quando la scheda e' in background (i timer vengono
    // strozzati dal browser): il controllo al rientro sulla scheda e' quello che intercetta
    // davvero il caso "scheda aperta da ore, deploy nel frattempo".
    const tabBecameVisible = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible')
    );

    merge(interval(POLL_INTERVAL_MS), tabBecameVisible, this.checkRequested)
      .pipe(
        switchMap(() => this.fetchVersion()),
        filter(version => !!this.currentVersion && !!version && version !== this.currentVersion),
        take(1)
      )
      .subscribe(() => this.showUpdateToast());
  }

  private fetchVersion() {
    return this.http
      .get<{ version: string }>('/version.json', {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      })
      .pipe(
        map(res => res.version),
        catchError(() => of(null))
      );
  }

  private showUpdateToast(): void {
    if (this.updateNotified) return;
    this.updateNotified = true;
    const message = this.translate.instant('versionUpdate.message');
    const actionLabel = this.translate.instant('versionUpdate.reload');
    this.notificationService.notify('info', message, {
      duration: 0,
      action: { label: actionLabel, fn: () => window.location.reload() }
    });
  }
}
